import React, { useRef, useState } from "react";
import { Upload, Loader2, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { adminAuth } from "../../lib/adminApi";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACCEPT = "image/jpeg,image/png,image/webp,image/gif,image/svg+xml";
const MAX_BYTES = 3 * 1024 * 1024;

// Resolves storage-relative URLs (e.g. "/api/media/abc") to a full URL
// for the <img> preview, while leaving full URLs (http://..., data:...) untouched.
const resolvePreview = (url) => {
    if (!url) return "";
    if (/^(https?:|data:|blob:)/i.test(url)) return url;
    if (url.startsWith("/")) return `${BACKEND_URL}${url}`;
    return url;
};

export const ImageUploadField = ({ value, onChange, testId, helpText, allowSvgPreview = true }) => {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const onPick = () => inputRef.current?.click();

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (file.size > MAX_BYTES) {
            toast.error("File too large", { description: "Max 3 MB. Try compressing the image." });
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const token = adminAuth.getToken();
            const res = await fetch(`${API}/admin/uploads`, {
                method: "POST",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                body: fd,
            });
            if (!res.ok) {
                let msg = "Upload failed";
                try {
                    const j = await res.json();
                    msg = j.detail || msg;
                } catch (_e) {}
                throw new Error(msg);
            }
            const data = await res.json();
            onChange(data.url);
            toast.success("Image uploaded");
        } catch (err) {
            toast.error(err.message || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const previewSrc = resolvePreview(value);
    const isSvg = previewSrc.toLowerCase().endsWith(".svg") || previewSrc.startsWith("data:image/svg");

    return (
        <div data-testid={testId || "image-upload-field"} className="space-y-3">
            {previewSrc ? (
                <div className="relative w-full max-w-xs rounded-lg overflow-hidden border border-white/10 bg-[#0a0a0a]">
                    {isSvg && !allowSvgPreview ? (
                        <div className="h-40 flex items-center justify-center text-zinc-500 font-inter text-xs">
                            <ImageIcon size={18} className="mr-2 text-gold" />
                            SVG uploaded
                        </div>
                    ) : (
                        <img
                            src={previewSrc}
                            alt="Preview"
                            className="block w-full max-h-48 object-contain"
                        />
                    )}
                    <button
                        type="button"
                        onClick={() => onChange("")}
                        aria-label="Remove image"
                        className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 border border-white/20 hover:border-red-400/50 hover:text-red-400 text-zinc-300 flex items-center justify-center transition-colors"
                    >
                        <X size={13} />
                    </button>
                </div>
            ) : null}

            <div className="flex flex-col sm:flex-row gap-2">
                <button
                    type="button"
                    onClick={onPick}
                    disabled={uploading}
                    data-testid={`${testId || "image-upload-field"}-pick`}
                    className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-[#C5A030] disabled:opacity-60 text-black font-medium font-inter text-sm px-5 py-2.5 rounded-lg transition-colors"
                >
                    {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                    {uploading ? "Uploading…" : previewSrc ? "Replace image" : "Upload image"}
                </button>
                <input
                    ref={inputRef}
                    type="file"
                    accept={ACCEPT}
                    onChange={onFile}
                    className="hidden"
                    data-testid={`${testId || "image-upload-field"}-input`}
                />
                <input
                    type="text"
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="…or paste image URL"
                    className="flex-1 bg-black/40 border border-white/10 focus:border-gold rounded-lg px-4 py-2.5 text-white outline-none transition-colors font-inter text-sm"
                    data-testid={`${testId || "image-upload-field"}-url`}
                />
            </div>

            <p className="font-inter text-xs text-zinc-500">
                {helpText || "JPEG, PNG, WEBP, GIF or SVG · max 3 MB. Stored on the studio server."}
            </p>
        </div>
    );
};

export default ImageUploadField;
